import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    AnankeClockIcon,
    AnankeLinkExternalIcon,
    AnankeModelVariantIcon,
    AnankeOfficeBuildingIcon,
    AnankePersonIcon,
    AnankeTagIcon,
} from "~/domainModels/workspaceSpecific/icons";
import {
    EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__REMARKS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__FRAME_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ORIGIN_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CAMPAIGN__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_CONTENT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_MEDIUM__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__UTM_TERM__COLUMN_ID,
    EmotoradEventTypes,
    EMOTORAD__EVENT__WALKINS__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CANCELLED_BY__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";

function normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
}

export function buildEmotradTestrideViewPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.testride as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };

    type ViewPanelColumn =
        | {
              id: Uuid;
              label: string;
              columnId: Uuid | string;
              isAdditionalColumn: true;
              columnType: "OttopilotEventContainer";
          }
        | {
              id: Uuid;
              label: string;
              columnId: Uuid | string;
              isAdditionalColumn: true;
              columnType: "OttopilotEvent";
              eventTemplateId: Uuid;
          }
        | {
              id: Uuid;
              label: string;
              columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              isAdditionalColumn: false;
              columnType: "OttopilotEvent";
              eventTemplateId: Uuid;
          }
        | {
              id: Uuid;
              label: string;
              columnId: "createdAt" | "updatedAt";
              isAdditionalColumn: false;
              columnType: "OttopilotEventContainer";
          };

    const toSectionColumns = (
        rows: Array<
            | {label: string; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotAuto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: false; columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy"}
            | {label: string; columnType: "OttopilotEventContainer"; isAdditionalColumn: false; columnId: "createdAt" | "updatedAt"}
        >,
    ): Array<ViewPanelColumn> => {
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    if (row.columnType === "OttopilotEvent") {
                        return {
                            id: `emotorad-${row.columnType}-${row.columnId}-${row.label}` as Uuid,
                            label: row.label,
                            columnId: row.columnId,
                            isAdditionalColumn: false as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return {
                        id: `emotorad-${row.columnType}-${row.columnId}-${row.label}` as Uuid,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: columnId,
                        label: row.label,
                        columnId: columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "OttopilotAuto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: journeyColumnId,
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: eventColumnId,
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const columnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (columnId === null) return null;
                return {
                    id: columnId,
                    label: row.label,
                    columnId: columnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                viewPanel: {
                    sections: [
                        {
                            id: uniqueUuid`emotorad-testride-view-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {label: "Customer Name", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Customer Name", "customer name"]},
                                {
                                    label: "Contact number",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Pincode",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode", "Customer PinCode"],
                                },
                                {label: "City", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["City", "Customer City", "customer city"]},
                                {label: "State", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["State", "Customer State", "customer state"]},
                                {
                                    label: "Height of Rider",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Height of rider", "Height of Rider", "Height"],
                                },
                                {
                                    label: "Purchase Timeline",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Purchase Timeline", "Purchase timeline"],
                                },
                                {
                                    label: "# vehicles",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["# Vehicles", "# vehicles", "No Of Vehicles", "No. of Vehicles"],
                                },
                                {
                                    label: "Action platform",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "Action platform", "Platform"],
                                },
                                {label: "Test Ride Date", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Test Ride Date", "Test ride Date"]},
                                {
                                    label: "Test ride Time",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Test Ride Time", "Test ride Time", "Test ride time"],
                                },
                                {
                                    label: "Last test Ride Date",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Last Test Ride Date", "Last test Ride Date"],
                                },
                                {label: "Test ride Count", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Test Ride Count", "Test ride Count"]},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-testride-view-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Model Variant",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient", "Model"],
                                },
                                {
                                    label: "Primary Riding purpose",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Primary Riding purpose", "Primary Riding Purpose"],
                                },
                                {label: "Exploring for", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Exploring for", "Exploring For"]},
                                {
                                    label: "Riding Frequency",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Riding Frequency", "Expected Riding Frequency"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-testride-view-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lifecycle Owner",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Lifecycle Owner", "Lead Owner", "Action Owner"],
                                },
                                {label: "Assigned Dealer", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Assigned Dealer", "Dealer Name"]},
                                {
                                    label: "Assigned Dealer Contact",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Contact Number", "Dealer Contact Number", "Assigned Dealer Contact"],
                                },
                                {
                                    label: "Assigned Dealer Email",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Email", "Dealer Email"],
                                },
                                {label: "Regional Manager", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["ZSM Name", "ZSM", "Regional Manager"]},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-testride-view-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Logged on",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
                                    displayNames: ["Logged On", "Logged on"],
                                },
                                {
                                    label: "Logged by",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__TESTRIDES__LOGGED_BY__COLUMN_ID,
                                    displayNames: ["Logged By", "Logged by"],
                                },
                                {
                                    label: "Scheduled on",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
                                    displayNames: ["Scheduled On", "Scheduled on"],
                                },
                                {
                                    label: "Scheduled by",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__TESTRIDES__SCHEDULED_BY__COLUMN_ID,
                                    displayNames: ["Scheduled By", "Scheduled by"],
                                },
                                {label: "Modified on", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                                {label: "Modified by", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {
                                    label: "Cancelled on",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
                                    displayNames: ["Cancelled On", "Cancelled on"],
                                },
                                {
                                    label: "Cancelled by",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__TESTRIDES__CANCELLED_BY__COLUMN_ID,
                                    displayNames: ["Cancelled By", "Cancelled by"],
                                },
                                {
                                    label: "Cancellation Reason",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancellation Reason", "Cancellation Remarks", "Cancelation Reason", "Cancelation Remarks"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-testride-view-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm Platform",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform", "Utm Platform"],
                                },
                                {
                                    label: "Utm Source",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source", "Utm Source"],
                                },
                                {
                                    label: "Utm Campaign",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign", "Utm Campaign"],
                                },
                                {
                                    label: "Utm Term",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term", "Utm Term"],
                                },
                                {
                                    label: "utm medium",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium", "Utm Medium"],
                                },
                                {
                                    label: "Utm Content",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content", "Utm Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

export function buildEmotradLeadViewPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.lead as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };

    type ViewPanelColumn =
        | {
              id: Uuid;
              label: string;
              columnId: Uuid | string;
              isAdditionalColumn: true;
              columnType: "OttopilotEventContainer";
          }
        | {
              id: Uuid;
              label: string;
              columnId: Uuid | string;
              isAdditionalColumn: true;
              columnType: "OttopilotEvent";
              eventTemplateId: Uuid;
          }
        | {
              id: Uuid;
              label: string;
              columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              isAdditionalColumn: false;
              columnType: "OttopilotEvent";
              eventTemplateId: Uuid;
          };

    const toSectionColumns = (
        rows: Array<
            | {label: string; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotAuto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: false; columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy"}
        >,
    ): Array<ViewPanelColumn> => {
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-${row.columnType}-${row.columnId}-${row.label}` as Uuid,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: columnId,
                        label: row.label,
                        columnId: columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "OttopilotAuto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: journeyColumnId,
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = row.columnId ?? getEventColumnId(row.displayNames);
                    if (eventColumnId !== null) {
                        return {
                            id: eventColumnId,
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const columnId = row.columnId ?? getEventColumnId(row.displayNames);
                if (columnId === null) return null;
                return {
                    id: columnId,
                    label: row.label,
                    columnId: columnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                viewPanel: {
                    sections: [
                        {
                            id: uniqueUuid`emotorad-lead-view-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {label: "Customer Name", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Customer Name", "customer name"]},
                                {
                                    label: "Contact number",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Gender",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Gender"],
                                    columnId: EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID,
                                },
                                {
                                    label: "Pincode",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode", "Customer PinCode"],
                                },
                                {
                                    label: "House Number",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "House Number",
                                        "House number",
                                        "House No",
                                        "House no",
                                        "House No.",
                                        "House no.",
                                        "Customer House No",
                                        "Customer House No.",
                                        "Customer House Number",
                                    ],
                                },
                                {label: "City", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["City", "Customer City", "customer city"]},
                                {label: "State", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["State", "Customer State", "customer state"]},
                                {label: "Landmark", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Landmark", "Customer Landmark"]},
                                {label: "Source Channels", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Source Channels", "Source Channel"]},
                                {
                                    label: "Purchase Timeline",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Purchase Timeline", "Purchase timeline"],
                                },
                                {
                                    label: "Height of Rider",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Height of rider", "Height of Rider", "Height"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-lead-view-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {label: "Model", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Model"]},
                                {label: "Model Variant", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Model Variant", "Model Varient"]},
                                {
                                    label: "Preferred Colour",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Preferred Colour", "Preferred Color", "Preferred colour"],
                                },
                                {
                                    label: "Is this your first cycle",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "Is this your first cycle",
                                        "Is this your first cyle",
                                        "Is this your first Cycle",
                                        "Is this your first Cycle?",
                                        "First Cycle",
                                    ],
                                },
                                {
                                    label: "Primary Riding purpose",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Primary Riding purpose", "Primary Riding Purpose"],
                                },
                                {label: "Current vehicle", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Current vehicle", "Current Vehicle"]},
                                {label: "Other Brands Considered", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Other Brands Considered"]},
                                {label: "Exploring for", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["Exploring for", "Exploring For"]},
                                {
                                    label: "Riding Frequency",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Riding Frequency", "Expected Riding Frequency"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-lead-view-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lifecycle Owner",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Lifecycle Owner", "Lead Owner", "Action Owner"],
                                },
                                {label: "Assigned Dealer", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Assigned Dealer", "Dealer Name"]},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-lead-view-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Created by (Role)",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Created by (Role)", "Created By Role", "Created by role", "Created by Role"],
                                },
                                {label: "Created by name", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdBy"},
                                {label: "Created on", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdAt"},
                                {label: "Modified on", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                                {label: "Modified by", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-lead-view-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm Platform",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform", "Utm Platform"],
                                },
                                {
                                    label: "Utm Source",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source", "Utm Source"],
                                },
                                {
                                    label: "Utm Campaign",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign", "Utm Campaign"],
                                },
                                {
                                    label: "Utm Term",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term", "Utm Term"],
                                },
                                {
                                    label: "utm medium",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium", "Utm Medium"],
                                },
                                {
                                    label: "Utm Content",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content", "Utm Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

export function buildEmotradWalkinViewPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.walkin as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    type ViewPanelColumn =
        | {id: Uuid; label: string; columnId: Uuid | string; isAdditionalColumn: true; columnType: "OttopilotEventContainer"}
        | {id: Uuid; label: string; columnId: Uuid | string; isAdditionalColumn: true; columnType: "OttopilotEvent"; eventTemplateId: Uuid}
        | {
              id: Uuid;
              label: string;
              columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              isAdditionalColumn: false;
              columnType: "OttopilotEvent";
              eventTemplateId: Uuid;
          };

    const toSectionColumns = (
        rows: Array<
            | {label: string; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotAuto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: false; columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy"}
        >,
    ): Array<ViewPanelColumn> => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-${row.columnType}-${row.columnId}-${row.label}` as Uuid,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "OttopilotAuto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                viewPanel: {
                    sections: [
                        {
                            id: uniqueUuid`emotorad-walkin-view-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {label: "Customer Name", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Customer Name", "customer name"]},
                                {
                                    label: "Contact number",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Pincode",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode", "Customer PinCode"],
                                },
                                {label: "City", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["City", "Customer City", "customer city"]},
                                {label: "State", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["State", "Customer State", "customer state"]},
                                {
                                    label: "Purchase Timeline",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Purchase Timeline", "Purchase timeline"],
                                },
                                {
                                    label: "Height of Rider",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Height of rider", "Height of Rider", "Height"],
                                },
                                {
                                    label: "Current platform",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Current Platform", "Action Platform", "Platform"],
                                },
                                {
                                    label: "Last walk-in date",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "Last Walk-in Date",
                                        "Last walk-in date",
                                        "Last Walkin Date",
                                        "Last walkin date",
                                        "Last VLH Date",
                                        "Last v.l.h date",
                                    ],
                                },
                                {
                                    label: "Walk-in Count",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Walk-in Count", "Walk in Count", "Walkin Count", "Walkin count", "With i Cyle?", "With i Cycle?"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-view-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Model variant",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient", "Model"],
                                },
                                {
                                    label: "Test ride taken ?",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "Test Ride Taken",
                                        "Test ride taken",
                                        "Test ride taken ?",
                                        "Test Ride Taken?",
                                        "test ride taken?",
                                        "Test ride Taken",
                                        "Is test ride taken",
                                        "Testride taken",
                                    ],
                                },
                                {
                                    label: "Primary riding purpose",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Primary Riding purpose", "Primary Riding Purpose"],
                                },
                                {
                                    label: "Exploring for",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKINS__EXPLORING_FOR__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Exploring for", "Exploring For"],
                                },
                                {
                                    label: "Other Brands Considered",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Other Brands Considered", "Other brands considered", "Other Brand Considered"],
                                },
                                {
                                    label: "Riding Frequency",
                                    columnType: "OttopilotAuto",
                                    isAdditionalColumn: true,
                                    displayNames: ["Riding Frequency", "Expected Riding Frequency", "Expected riding frequency"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-view-dispositions`,
                            displayName: "Dispositions",
                            iconId: AnankeTagIcon,
                            iconColor: "var(--ds-icon-accent-teal)",
                            iconBackgroundColor: "var(--ds-background-accent-teal-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "L1 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKIN__L1_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L1 Disposition", "L1 disposition"],
                                },
                                {
                                    label: "L2 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKIN__L2_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L2 Disposition", "L2 disposition"],
                                },
                                {
                                    label: "L3 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKIN__L3_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L3 Disposition", "L3 disposition"],
                                },
                                {
                                    label: "Remarks",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__WALKINS__REMARKS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Remarks", "Disposition Remarks"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-view-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lifecycle Owner",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Lifecycle Owner", "Lead Owner", "Action Owner"],
                                },
                                {label: "Assigned Dealer", columnType: "OttopilotEventContainer", isAdditionalColumn: true, displayNames: ["Assigned Dealer", "Dealer Name"]},
                                {
                                    label: "Assigned Dealer Contact",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Contact Number", "Dealer Contact Number", "Assigned Dealer Contact"],
                                },
                                {
                                    label: "Assigned Dealer Email",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Email", "Dealer Email"],
                                },
                                {label: "KAM", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["KAM Name", "KAM"]},
                                {label: "RM", columnType: "OttopilotAuto", isAdditionalColumn: true, displayNames: ["ZSM Name", "ZSM", "RM Name", "Regional Manager"]},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-view-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Logged on",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__WALKINS__LOGGED_ON__COLUMN_ID,
                                    displayNames: ["Logged On", "Logged on"],
                                },
                                {
                                    label: "Logged by",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__WALKINS__LOGGED_BY__COLUMN_ID,
                                    displayNames: ["Logged By", "Logged by"],
                                },
                                {
                                    label: "Scheduled on",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__WALKINS__SCHEDULED_ON__COLUMN_ID,
                                    displayNames: ["Scheduled On", "Scheduled on"],
                                },
                                {
                                    label: "Scheduled by",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__WALKINS__SCHEDULED_BY__COLUMN_ID,
                                    displayNames: ["Scheduled By", "Scheduled by"],
                                },
                                {label: "Modified on", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                                {label: "Modified by", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {
                                    label: "Cancelled on",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__WALKINS__CANCELLED_ON__COLUMN_ID,
                                    displayNames: ["Cancelled On", "Cancelled on"],
                                },
                                {
                                    label: "Cancelled by",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    columnId: EMOTORAD__EVENT__WALKINS__CANCELLED_BY__COLUMN_ID,
                                    displayNames: ["Cancelled By", "Cancelled by"],
                                },
                                {
                                    label: "Cancellation Reason",
                                    columnType: "OttopilotEvent",
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancellation Reason", "Cancellation Remarks", "Cancelation Reason", "Cancelation Remarks"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-walkin-view-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm Platform",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform", "Utm Platform"],
                                },
                                {
                                    label: "Utm Source",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source", "Utm Source"],
                                },
                                {
                                    label: "Utm Campaign",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign", "Utm Campaign"],
                                },
                                {
                                    label: "Utm Term",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term", "Utm Term"],
                                },
                                {
                                    label: "utm medium",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium", "Utm Medium"],
                                },
                                {
                                    label: "Utm Content",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content", "Utm Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

export function buildEmotradCallViewPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.call as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    type ViewPanelColumn =
        | {id: Uuid; label: string; columnId: Uuid | string; isAdditionalColumn: true; columnType: "OttopilotEventContainer"}
        | {id: Uuid; label: string; columnId: Uuid | string; isAdditionalColumn: true; columnType: "OttopilotEvent"; eventTemplateId: Uuid}
        | {
              id: Uuid;
              label: string;
              columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              isAdditionalColumn: false;
              columnType: "OttopilotEvent";
              eventTemplateId: Uuid;
          };

    const toSectionColumns = (
        rows: Array<
            | {label: string; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotAuto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: false; columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy"}
        >,
    ): Array<ViewPanelColumn> => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-call-${row.columnType}-${row.columnId}-${row.label}` as Uuid,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "OttopilotAuto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                viewPanel: {
                    sections: [
                        {
                            id: uniqueUuid`emotorad-call-view-call-status`,
                            displayName: "Call status",
                            iconId: AnankeTagIcon,
                            iconColor: "var(--ds-icon-accent-teal)",
                            iconBackgroundColor: "var(--ds-background-accent-teal-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Call Status",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Call Status", "Status"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-view-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name", "Account"],
                                },
                                {
                                    label: "Contact Number",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Customer Pincode",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode"],
                                },
                                {
                                    label: "Customer State",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer State", "State"],
                                },
                                {
                                    label: "Customer City",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer City", "City"],
                                },
                                {
                                    label: "Action Platform",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "Current Platform", "Platform"],
                                },
                                {
                                    label: "KAM",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["KAM Name", "KAM"],
                                },
                                {
                                    label: "RM",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["RM Name", "ZSM Name", "ZSM", "Regional Manager"],
                                },
                                {
                                    label: "Test Ride Taken",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Test Ride Taken", "Test ride taken", "Test ride taken ?", "Test Ride Taken?"],
                                },
                                {
                                    label: "Purchase Timeline",
                                    columnType: "OttopilotAuto",
                                    columnId: EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Purchase Timeline", "Purchase timeline"],
                                },
                                {
                                    label: "Exploring For",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__EXPLORING_FOR__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Exploring for", "Exploring For"],
                                },
                                {
                                    label: "Primary Riding Purpose",
                                    columnType: "OttopilotAuto",
                                    columnId: EMOTORAD__EVENT__CALLS__PRIMARY_RIDING_PURPOSE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Primary Riding purpose", "Primary Riding Purpose"],
                                },
                                {
                                    label: "Height of Rider",
                                    columnType: "OttopilotAuto",
                                    columnId: EMOTORAD__EVENT__CALLS__HEIGHT_OF_RIDER__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Height of rider", "Height of Rider"],
                                },
                                {
                                    label: "Expected Riding Frequency",
                                    columnType: "OttopilotAuto",
                                    columnId: EMOTORAD__EVENT__CALLS__EXPECTED_RIDING_FREQUENCY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Riding Frequency", "Expected Riding Frequency", "Expected riding frequency"],
                                },
                                {
                                    label: "Remarks",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__LOGGED_CALL_REMARKS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Remarks", "Call Remarks", "Logged Call Remarks"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-view-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Model Variant",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__MODEL_VARIANT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient", "Model"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-view-dispositions`,
                            displayName: "Dispositions",
                            iconId: AnankeTagIcon,
                            iconColor: "var(--ds-icon-accent-teal)",
                            iconBackgroundColor: "var(--ds-background-accent-teal-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "L1 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALL__L1_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L1 Disposition", "L1 disposition"],
                                },
                                {
                                    label: "L2 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALL__L2_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L2 Disposition", "L2 disposition"],
                                },
                                {
                                    label: "L3 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALL__L3_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L3 Disposition", "L3 disposition"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-view-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Last Call Date",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Last Call Date", "Last call date"],
                                },
                                {
                                    label: "Call Count",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__CALLS_COUNT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Call Count", "Calls Count", "call count"],
                                },
                                {
                                    label: "Scheduled By",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled By", "Scheduled by"],
                                },
                                {
                                    label: "Scheduled On",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled On", "Scheduled on"],
                                },
                                {
                                    label: "Logged By",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Logged By", "Logged by"],
                                },
                                {
                                    label: "Logged On",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Logged On", "Logged on"],
                                },
                                {
                                    label: "Cancelled By",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled By", "Cancelled by"],
                                },
                                {
                                    label: "Cancelled On",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled On", "Cancelled on"],
                                },
                                {label: "Modified By", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {label: "Modified On", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-view-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lifecycle Owner",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Lifecycle Owner", "Lead Owner"],
                                },
                                {
                                    label: "Assigned Dealer",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer", "Dealer Name"],
                                },
                                {
                                    label: "Assigned Dealer Contact",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Contact", "Assigned Dealer Contact Number"],
                                },
                                {
                                    label: "Assigned Dealer Email",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Email"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-call-view-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm_platform",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform"],
                                },
                                {
                                    label: "Utm_source",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source"],
                                },
                                {
                                    label: "Utm_medium",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium"],
                                },
                                {
                                    label: "Utm_campaign",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                                },
                                {
                                    label: "Utm_term",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_TERM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term"],
                                },
                                {
                                    label: "Utm_content",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

export function buildEmotradSalesViewPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (EmotoradEventTypes.sales as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    type ViewPanelColumn =
        | {id: Uuid; label: string; columnId: Uuid | string; isAdditionalColumn: true; columnType: "OttopilotEventContainer"}
        | {id: Uuid; label: string; columnId: Uuid | string; isAdditionalColumn: true; columnType: "OttopilotEvent"; eventTemplateId: Uuid}
        | {
              id: Uuid;
              label: string;
              columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              isAdditionalColumn: false;
              columnType: "OttopilotEvent";
              eventTemplateId: Uuid;
          };

    const toSectionColumns = (
        rows: Array<
            | {label: string; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotAuto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: false; columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy"}
        >,
    ): Array<ViewPanelColumn> => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `emotorad-sales-${row.columnType}-${row.columnId}-${row.label}` as Uuid,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "OttopilotAuto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                viewPanel: {
                    sections: [
                        {
                            id: uniqueUuid`emotorad-sales-view-customer-details`,
                            displayName: "Customer details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name", "Account"],
                                },
                                {
                                    label: "Contact Number",
                                    columnType: "OttopilotEventContainer",
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Customer Pincode",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Pincode", "Pincode"],
                                },
                                {
                                    label: "Sale Status",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Sale Status", "Status", "Sales Status"],
                                },
                                {
                                    label: "Source Channel",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__SOURCE_CHANNEL__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Source Channel", "source channel"],
                                },
                                {
                                    label: "Customer State",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer State", "State"],
                                },
                                {
                                    label: "Customer City",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer City", "City"],
                                },
                                {
                                    label: "Customer Zone",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Zone", "Zone"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-sales-view-product-details`,
                            displayName: "Product details",
                            iconId: AnankeModelVariantIcon,
                            iconColor: "var(--ds-icon-accent-orange)",
                            iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Model",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Model"],
                                },
                                {
                                    label: "Frame Number",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__FRAME_NUMBER__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Frame Number", "frame number", "Frame no", "Chassis"],
                                },
                                {
                                    label: "Model Variant",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Model Variant", "Model Varient", "Model variant"],
                                },
                                {
                                    label: "Preferred Color",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Preferred Color", "preferred color", "Color"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-sales-view-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Action Platform",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "Current Platform", "Platform"],
                                },
                                {
                                    label: "Verified On",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CLOSED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Verified On", "verified on", "Closed On", "closed on"],
                                },
                                {
                                    label: "Created by (Role)",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Created by (Role)", "Created By Role", "Created by role"],
                                },
                                {
                                    label: "Created by (Name)",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Created by (Name)", "Created By Name", "Created by name"],
                                },
                                {label: "Created On", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "createdAt"},
                                {label: "Modified By", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {label: "Modified On", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-sales-view-showroom-details`,
                            displayName: "Showroom Details",
                            iconId: AnankeOfficeBuildingIcon,
                            iconColor: "var(--ds-icon-accent-green)",
                            iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Assigned Dealer",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer", "Dealer Name"],
                                },
                                {
                                    label: "Assigned Dealer Contact",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: [
                                        "Assigned Dealer Contact",
                                        "Assigned Dealer Contact Number",
                                        "Dealer Contact Number",
                                        "Showroom Contact",
                                        "Dealer Contact",
                                    ],
                                },
                                {
                                    label: "Assigned Dealer Email",
                                    columnType: "OttopilotEventContainer",
                                    columnId: EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Dealer Email", "Showroom Mail", "Showroom Email", "Dealer Email", "Email Address"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`emotorad-sales-view-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm_platform",
                                    columnType: "OttopilotAuto",
                                    columnId: EMOTORAD__EVENT__SALES__ORIGIN_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform", "Origin Platform"],
                                },
                                {
                                    label: "Utm_source",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_SOURCE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source"],
                                },
                                {
                                    label: "Utm_medium",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_MEDIUM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium"],
                                },
                                {
                                    label: "Utm_campaign",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_CAMPAIGN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                                },
                                {
                                    label: "Utm_term",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_TERM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term"],
                                },
                                {
                                    label: "Utm_content",
                                    columnType: "OttopilotEvent",
                                    columnId: EMOTORAD__EVENT__SALES__UTM_CONTENT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content"],
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}
